import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, FileText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { PainelRoboFaturas } from './PainelRoboFaturas';

const BASE = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

interface LinhaFunil {
  grupo: 'carteira' | 'primeira' | 'seguintes';
  deveria: number;
  temLinha: number | null;
  chegou: number;
  chegouNele: number;
  chamou: number | null;
  pagou: number | null;
}
interface MetaSafra { safra: string; pagas: number; pedidos: number; pct: number | null }
interface PainelFaturas {
  competencia: string;
  anterior: string;
  competencias: string[];
  corrente: boolean;
  tipoPessoa: 'PJ' | 'PF';
  linhas: LinhaFunil[];
  travaCarteira: { semLinha: number; esperandoBee: number; beeNaoDevolveu: number; vaiPedir: number; outro: number; emailNaoSaiu: number } | null;
  travaPrimeira: { aindaNaoExiste: number; faltaBaixar: number; naoPago: number };
  comparacao: {
    chegou: { atual: number; anterior: number };
    chegouNele: { atual: number; anterior: number };
    chamou: { atual: number; anterior: number };
    pagouPrimeira: { atual: MetaSafra | null; anterior: MetaSafra | null };
  } | null;
  hoje: { pedimos: number; pdfs: number; contaNova: number; copia: number; recusada: number; emailSaiu: number; chamaram: number };
  consultores: { id: string | null; nome: string; equipe: string | null; carteira: number; chegou: number; semLinha: number; chegouNele: number; chamou: number }[];
  geradoEm: string;
}

async function buscarPainelFaturas(competencia: string | null, tipoPessoa: 'PJ' | 'PF'): Promise<PainelFaturas> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada — faça login novamente.');
  const p = new URLSearchParams({ tipoPessoa });
  if (competencia) p.set('competencia', competencia);
  const r = await fetch(`${BASE()}/v1/faturas/painel?${p}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.error || 'Não foi possível montar o painel das faturas.');
  return body.data as PainelFaturas;
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MESES_LONGOS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const mesCurto = (c: string) => MESES[Number(c.slice(5, 7)) - 1] || c;
const mesLongo = (c: string) => MESES_LONGOS[Number(c.slice(5, 7)) - 1] || c;
const mesSeguinte = (c: string) => {
  const m = Number(c.slice(5, 7));
  return MESES_LONGOS[m % 12];
};
const fmt = (v: number) => v.toLocaleString('pt-BR');
const pct = (parte: number, todo: number) => (todo > 0 ? `${((parte / todo) * 100).toLocaleString('pt-BR', { maximumFractionDigits: parte / todo < 0.01 ? 1 : 0 })}%` : '—');
const pontos = (a: number, b: number, todo: number) => {
  if (!todo) return null;
  const d = Math.round(((a - b) / todo) * 100);
  return d;
};

const ROTULO: Record<LinhaFunil['grupo'], [string, string]> = {
  carteira: ['Carteira', 'base da Claro'],
  primeira: ['1ª fatura', 'ativou há pouco'],
  seguintes: ['2ª a 6ª conta', 'até 180 dias'],
};

const VERDE = 'text-[#075E54] dark:text-[#25D366]';
const BORDO = 'text-[#7A2235] dark:text-[#C87484]';

/**
 * PAINEL DAS FATURAS DO TOPO (Douglas 02/10/2026) — o mesmo desenho do painel
 * de SMS (PainelSmsTopo). Um mês só e o caminho da conta da esquerda para a
 * direita: cada linha é um tipo de cliente, cada coluna uma etapa. Todos os
 * números vêm de /v1/faturas/painel (painel-faturas.service.ts).
 */
export function PainelFaturasTopo({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const navigate = useNavigate();
  const [competencia, setCompetencia] = useState<string | null>(null);
  const [tipoPessoa, setTipoPessoa] = useState<'PJ' | 'PF'>('PJ');
  const [roboAberto, setRoboAberto] = useState(false);
  const painel = useQuery({
    queryKey: ['faturas', 'painel', competencia, tipoPessoa],
    enabled: aberto,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    queryFn: () => buscarPainelFaturas(competencia, tipoPessoa),
  });
  const d = painel.data;
  const comp = d?.competencia ?? competencia;
  const irPara = (rota: string) => { onFechar(); navigate(rota); };

  const segmentado = 'flex rounded-lg bg-white/[0.12] p-[2px] text-[11px]';
  const segBtn = (ativo: boolean) => cn('rounded-md px-2 py-[3px] focus-visible:ring-2 focus-visible:ring-white/60',
    ativo ? 'bg-white font-semibold text-[#5B0A0D]' : 'text-white hover:bg-white/[0.12]');
  const th = 'px-1.5 py-1.5 text-right text-[10px] font-bold uppercase tracking-[0.6px] text-muted-foreground';
  const td = 'px-1.5 py-2 border-t border-border/60 text-right tabular-nums';
  const sub = 'block text-[10px] font-normal text-muted-foreground';
  const caixa = 'rounded-xl border border-border bg-card px-3 py-2.5';
  const titulo = 'mb-1.5 text-[10px] font-bold uppercase tracking-[1px] text-muted-foreground';
  const linhaTrava = 'flex items-center justify-between py-[3px] text-[12px]';

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!v) onFechar(); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-[760px] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0 rounded-2xl sm:rounded-2xl bg-background [&>button]:hidden">
        <div className="flex shrink-0 items-center gap-3 bg-[linear-gradient(135deg,#4A1320_0%,#5B0A0D_55%,#6E1626_100%)] px-4 py-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-white/[0.12] text-white"><FileText className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display text-[15px] font-bold tracking-[-0.3px] text-white">
              Faturas · conta de {comp ? mesLongo(comp) : '…'}
            </DialogTitle>
            <p className="truncate text-[10.5px] text-white/65">
              {comp ? `Vence em ${mesSeguinte(comp)} · ` : ''}{tipoPessoa === 'PJ' ? 'CNPJ' : 'CPF'} · todas as equipes
            </p>
          </div>
          <div className={segmentado} role="group" aria-label="Segmento">
            {(['PJ', 'PF'] as const).map((t) => (
              <button key={t} type="button" aria-pressed={tipoPessoa === t} onClick={() => setTipoPessoa(t)} className={segBtn(tipoPessoa === t)}>
                {t === 'PJ' ? 'CNPJ' : 'CPF'}
              </button>
            ))}
          </div>
          <div className={segmentado} role="group" aria-label="Mês da conta">
            {(d?.competencias ?? []).map((c) => (
              <button key={c} type="button" aria-pressed={comp === c} onClick={() => setCompetencia(c)} className={segBtn(comp === c)}>
                {mesCurto(c)}
              </button>
            ))}
          </div>
          <button type="button" aria-label="Fechar" onClick={onFechar}
            className="grid h-[26px] w-[26px] place-items-center rounded-lg border border-white/[0.14] bg-white/[0.12] text-white hover:bg-white/[0.22]">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-4 py-3">
          {painel.isLoading && <p className="text-[11px] text-muted-foreground">Montando o painel das faturas…</p>}
          {painel.error && (
            <p role="alert" className="text-[11px] text-destructive">
              {(painel.error as Error).message}{' '}
              <button type="button" className="underline" onClick={() => void painel.refetch()}>Tentar de novo</button>
            </p>
          )}
          {d && <>
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full table-fixed text-[13px]">
                <thead className="bg-[#FAF8F4] dark:bg-[#241A1A]">
                  <tr>
                    <th className={cn(th, 'w-[22%] text-left')}>Cliente</th>
                    <th className={th}>Deveria ter</th>
                    <th className={th}>Tem linha</th>
                    <th className={th}>Conta chegou</th>
                    <th className={th}>Chegou nele</th>
                    <th className={th}>Chamou</th>
                    <th className={th}>Pagou</th>
                  </tr>
                </thead>
                <tbody>
                  {d.linhas.map((l) => (
                    <tr key={l.grupo}>
                      <td className={cn(td, 'text-left font-semibold')}>{ROTULO[l.grupo][0]}<span className={sub}>{ROTULO[l.grupo][1]}</span></td>
                      <td className={td}>{fmt(l.deveria)}</td>
                      <td className={td}>{l.temLinha == null ? <span className="text-[11px] text-muted-foreground">não medido</span> : <>{fmt(l.temLinha)}<span className={sub}>{pct(l.temLinha, l.deveria)}</span></>}</td>
                      <td className={cn(td, VERDE)}>{fmt(l.chegou)}<span className={sub}>{pct(l.chegou, l.deveria)}</span></td>
                      <td className={td}>{fmt(l.chegouNele)}<span className={sub}>{pct(l.chegouNele, l.deveria)}</span></td>
                      <td className={cn(td, l.chamou != null && BORDO)}>{l.chamou == null ? <span className="text-[11px] text-muted-foreground">não medido</span> : <>{fmt(l.chamou)}<span className={sub}>{pct(l.chamou, l.deveria)}</span></>}</td>
                      <td className={td}>{l.pagou == null ? <span className="text-[11px] text-muted-foreground">não medido</span> : <>{fmt(l.pagou)}<span className={sub}>{pct(l.pagou, l.deveria)}</span></>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {d.travaCarteira && (
                <div className={caixa}>
                  <p className={titulo}>Onde a carteira trava · hoje</p>
                  <p className={linhaTrava}><span>Sem linha</span><span className={cn('tabular-nums font-semibold', BORDO)}>{fmt(d.travaCarteira.semLinha)}</span></p>
                  <p className={linhaTrava}><span>Bee não devolveu</span><span className={cn('tabular-nums font-semibold', BORDO)}>{fmt(d.travaCarteira.beeNaoDevolveu)}</span></p>
                  <p className={linhaTrava}><span>Esperando o Bee</span><span className="tabular-nums">{fmt(d.travaCarteira.esperandoBee)}</span></p>
                  <p className={linhaTrava}><span>Vai pedir agora</span><span className="tabular-nums">{fmt(d.travaCarteira.vaiPedir)}</span></p>
                  {d.travaCarteira.outro > 0 && <p className={linhaTrava}><span>Outro motivo</span><span className="tabular-nums">{fmt(d.travaCarteira.outro)}</span></p>}
                  <p className={cn(linhaTrava, 'mt-1 border-t border-border/60 pt-1')}><span>Conta chegou, e-mail não saiu</span><span className={cn('tabular-nums font-semibold', BORDO)}>{fmt(d.travaCarteira.emailNaoSaiu)}</span></p>
                </div>
              )}
              <div className={caixa}>
                <p className={titulo}>Onde a 1ª fatura trava</p>
                <p className={linhaTrava}><span>Conta ainda não existe</span><span className="tabular-nums text-muted-foreground">{fmt(d.travaPrimeira.aindaNaoExiste)}</span></p>
                <p className={linhaTrava}><span>Falta baixar</span><span className={cn('tabular-nums font-semibold', BORDO)}>{fmt(d.travaPrimeira.faltaBaixar)}</span></p>
                <p className={linhaTrava}><span>Não pago</span><span className={cn('tabular-nums font-semibold', BORDO)}>{fmt(d.travaPrimeira.naoPago)}</span></p>
              </div>
            </div>

            {d.comparacao && (() => {
              const c = d.comparacao;
              const base = d.linhas.find((l) => l.grupo === 'carteira')?.deveria ?? 0;
              const barras: [string, { atual: number; anterior: number }][] = [['Conta chegou', c.chegou], ['E-mail ao cliente', c.chegouNele]];
              return (
                <div className={caixa}>
                  <div className="mb-1 flex items-baseline justify-between">
                    <p className={cn(titulo, 'mb-0')}>{mesLongo(d.competencia)} comparado a {mesLongo(d.anterior)} · carteira</p>
                    <p className="flex items-center gap-2 text-[10.5px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm bg-[#B4808C]" />{mesCurto(d.anterior)}</span>
                      <span className="inline-flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm bg-primary" />{mesCurto(d.competencia)}</span>
                    </p>
                  </div>
                  {barras.map(([rotulo, v]) => {
                    const p = pontos(v.atual, v.anterior, base);
                    return (
                      <div key={rotulo} className="grid grid-cols-[130px_minmax(0,1fr)_64px] items-center gap-2 py-1">
                        <span className="text-[12px]">{rotulo}<span className={sub}>{fmt(v.anterior)} → {fmt(v.atual)}</span></span>
                        <div className="space-y-[3px]">
                          <div className="h-[7px] overflow-hidden rounded-full bg-border"><i className="block h-full bg-[#B4808C]" style={{ width: `${base ? (v.anterior / base) * 100 : 0}%` }} /></div>
                          <div className="h-[7px] overflow-hidden rounded-full bg-border"><i className="block h-full bg-primary" style={{ width: `${base ? (v.atual / base) * 100 : 0}%` }} /></div>
                        </div>
                        <span className={cn('text-right text-[12px] tabular-nums', p != null && p > 0 ? VERDE : p != null && p < 0 ? BORDO : 'text-muted-foreground')}>
                          {p == null ? '—' : `${p > 0 ? '+' : ''}${p} pts`}
                        </span>
                      </div>
                    );
                  })}
                  <div className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 py-1 text-[12px]">
                    <span>Cliente chamou</span>
                    <span className="tabular-nums">{fmt(c.chamou.anterior)} → {fmt(c.chamou.atual)}</span>
                  </div>
                  <div className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 py-1 text-[12px]">
                    <span>Pagou a 1ª conta</span>
                    <span className="tabular-nums">
                      {c.pagouPrimeira.atual?.pct != null
                        ? <>{c.pagouPrimeira.atual.pct.toLocaleString('pt-BR')}% <span className="text-[10.5px] text-muted-foreground">· safra de {mesLongo(c.pagouPrimeira.atual.safra)} ({fmt(c.pagouPrimeira.atual.pagas)} de {fmt(c.pagouPrimeira.atual.pedidos)})</span></>
                        : <span className="text-muted-foreground">sem safra fechada</span>}
                      {c.pagouPrimeira.anterior?.pct != null && <span className="text-[10.5px] text-muted-foreground"> · antes {c.pagouPrimeira.anterior.pct.toLocaleString('pt-BR')}%</span>}
                    </span>
                  </div>
                </div>
              );
            })()}

            <div className="flex items-center gap-2">
              <p className="w-[44px] shrink-0 text-[10px] font-bold uppercase tracking-[1px] text-muted-foreground">Hoje</p>
              <div className="grid flex-1 grid-cols-3 gap-1.5 sm:grid-cols-6">
                {([
                  ['Pedimos ao Bee', d.hoje.pedimos, ''],
                  ['PDFs recebidos', d.hoje.pdfs, ''],
                  ['Conta nova', d.hoje.contaNova, VERDE],
                  ['Cópia', d.hoje.copia, 'text-muted-foreground'],
                  ['E-mail saiu', d.hoje.emailSaiu, ''],
                  ['Chamaram', d.hoje.chamaram, VERDE],
                ] as const).map(([r, v, cor]) => (
                  <div key={r} className="rounded-lg bg-[#FAF8F4] px-2 py-1.5 dark:bg-[#241A1A]">
                    <p className="truncate text-[10px] text-muted-foreground">{r}</p>
                    <p className={cn('font-display text-[16px] font-bold tabular-nums', cor)}>{fmt(v)}</p>
                  </div>
                ))}
              </div>
            </div>

            {d.consultores.length > 0 && (
              <div className={caixa}>
                <p className={titulo}>Por consultor dono · carteira</p>
                <table className="w-full table-fixed text-[12px]">
                  <thead>
                    <tr>
                      <th className={cn(th, 'w-[30%] text-left')}>Consultor</th>
                      <th className={th}>Carteira</th>
                      <th className={th}>Conta chegou</th>
                      <th className={th}>Sem linha</th>
                      <th className={th}>E-mail</th>
                      <th className={th}>Chamou</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.consultores.map((c) => (
                      <tr key={c.id ?? 'sem-dono'}>
                        <td className={cn(td, 'truncate text-left')} title={c.nome}>{c.nome}{c.equipe && <span className="ml-1 text-[10px] text-muted-foreground">{c.equipe}</span>}</td>
                        <td className={td}>{fmt(c.carteira)}</td>
                        <td className={cn(td, VERDE)}>{fmt(c.chegou)}<span className={sub}>{pct(c.chegou, c.carteira)}</span></td>
                        <td className={cn(td, c.semLinha > 0 && BORDO)}>{fmt(c.semLinha)}</td>
                        <td className={td}>{fmt(c.chegouNele)}</td>
                        <td className={cn(td, c.chamou > 0 && cn('font-semibold', VERDE))}>{fmt(c.chamou)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Mês = a conta que fecha no mês e vence no seguinte. Percentual é sobre "deveria ter". Chegou nele = o e-mail da conta saiu para o cliente (na 1ª fatura e nas seguintes, e-mail ou WhatsApp).
              Chamou = o cliente clicou no botão do e-mail e mandou a mensagem no WhatsApp; abrir o link sem mandar não conta. "Onde trava" mostra a situação de hoje, só no mês corrente; cada cliente entra em um motivo só.
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => irPara('/ciclo-faturas')}
                className="inline-flex h-[28px] items-center gap-1 rounded-lg border border-border px-2.5 text-[11px] font-semibold text-primary hover:bg-muted/50">
                Abrir o Ciclo de Faturas <ArrowRight className="h-3 w-3" />
              </button>
              <button type="button" onClick={() => setRoboAberto(true)}
                className="inline-flex h-[28px] items-center gap-1 rounded-lg border border-border px-2.5 text-[11px] font-semibold text-primary hover:bg-muted/50">
                Ver o robô e o que falta <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </>}
        </div>
        {roboAberto && <PainelRoboFaturas aberto onFechar={() => setRoboAberto(false)} onIr={(rota) => { setRoboAberto(false); irPara(rota); }} />}
      </DialogContent>
    </Dialog>
  );
}
